import { AimOutlined, CustomerServiceOutlined, SoundOutlined, TranslationOutlined } from "@ant-design/icons";
import { capitalize } from "@buzzeasy/shared-frontend-utilities";
import { Alert, Progress, Slider, Space, Switch, theme } from "antd";
import { ReactElement, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useDependantState from "../../hooks/useDependantState";
import { generateColorGradient } from "../../utils/colorUtils";
import SettingGroup from "../presenters/SettingGroup";
import SettingPanel from "../presenters/SettingPanel";
import { TranscriptHubContext } from "../providers/TranscriptHubProvider";

const { useToken } = theme;

export interface SettingSideBarProps {
  sidebarExpanded: boolean;
}

export default function SettingSideBar({ sidebarExpanded }: SettingSideBarProps): ReactElement {
  const { isLoading, isError, transcriptItems, volume, changeVolume, isTranslationEnabled, changeTranslationEnabled, isCustomerAudioEnabled, changeCustomerAudioEnabled, isChangingCustomerAudioStatusDisabled } = useContext(TranscriptHubContext);

  const { t } = useTranslation();
  const { token } = useToken();

  const [displayedVolume, setDisplayedVolume, resetDisplayedVolume] = useDependantState(volume);
  const [displayedIsTranslationEnabled, setDisplayedIsTranslationEnabled, resetDisplayedIsTranslationEnabled] = useDependantState(isTranslationEnabled);
  const [displayedIsCustomerAudioEnabled, setDisplayedIsCustomerAudioEnabled, resetDisplayedIsCustomerAudioEnabled] = useDependantState(isCustomerAudioEnabled);

  const handleVolumeChange = useCallback(
    (vol: number) => setDisplayedVolume(vol),
    [setDisplayedVolume],
  );

  const handleVolumeChangeComplete = useCallback(
    async (vol: number) => {
      const success = await changeVolume(vol);

      if (!success)
        resetDisplayedVolume();
    },
    [changeVolume, resetDisplayedVolume],
  );

  // internal state and logic is used for the switch to show the correct state immediately without waiting for the server response
  const handleTranslationEnabledChange = useCallback(
    async (isEnabled: boolean) => {
      setDisplayedIsTranslationEnabled(isEnabled);
      const success = await changeTranslationEnabled(isEnabled);

      if (!success)
        resetDisplayedIsTranslationEnabled();
    },
    [changeTranslationEnabled, resetDisplayedIsTranslationEnabled, setDisplayedIsTranslationEnabled],
  );

  const handleCustomerAudioEnabledChange = useCallback(
    async (isEnabled: boolean) => {
      setDisplayedIsCustomerAudioEnabled(isEnabled);
      const success = await changeCustomerAudioEnabled(isEnabled);

      if (!success)
        resetDisplayedIsCustomerAudioEnabled();
    },
    [changeCustomerAudioEnabled, resetDisplayedIsCustomerAudioEnabled, setDisplayedIsCustomerAudioEnabled],
  );

  const confidencePercentage = useMemo(
    () => {
      const customerTranscriptItems = transcriptItems.filter(item => item.source.participant === "Customer");
      const totalConfidence = customerTranscriptItems.reduce((sum, item) => sum + item.confidence, 0);
      const avgConfidenceRatio = totalConfidence / (customerTranscriptItems.length || 1);
      return Math.round(avgConfidenceRatio * 100);
    },
    [transcriptItems],
  );

  const confidenceColorGradient = useMemo(
    () => generateColorGradient([token.colorError, token.colorWarning, token.colorSuccess], 101), // 101 = 0-100%
    [token.colorError, token.colorSuccess, token.colorWarning],
  );

  return (
    <SettingSideBarBox>
      <SettingPanel
        compact={!sidebarExpanded}
        title={t("sidebar.confidenceLevel")}
        tooltip={t("sidebar.confidenceLevelTooltip")}
        icon={<AimOutlined />}
        value={percentageStr(confidencePercentage)}
        showValueOnExpended
      >
        <Progress
          percent={confidencePercentage}
          showInfo={false}
          strokeColor={confidenceColorGradient[confidencePercentage]}
          aria-label={t("sidebar.confidenceLevel")}
        />
      </SettingPanel>

      <SettingPanel
        compact={!sidebarExpanded}
        title={t("sidebar.translationEnabled")}
        tooltip={t("sidebar.translationEnabledTooltip")}
        icon={<TranslationOutlined />}
        value={capitalize(t(isTranslationEnabled ? "terms.on" : "terms.off"))}
        showValueOnExpended
      >
        <Switch
          size="small"
          disabled={isLoading || isError}
          loading={displayedIsTranslationEnabled !== isTranslationEnabled}
          checked={displayedIsTranslationEnabled}
          onChange={handleTranslationEnabledChange}
          checkedChildren={capitalize(t("terms.on"))}
          unCheckedChildren={capitalize(t("terms.off"))}
        />
      </SettingPanel>

      <SettingGroup label={t("sidebar.customerVoice.label")} sidebarExpanded={sidebarExpanded}>
        {isChangingCustomerAudioStatusDisabled
          ? (
              <Alert
                type="warning"
                showIcon
                message={t("sidebar.customerVoice.settingsUnavailableDueToConfiguration")}
                style={{ margin: 0 }}
              />
            )
          : (
              <Space direction="vertical" size="small" style={{ display: "flex" }}>
                <SettingPanel
                  compact={!sidebarExpanded}
                  title={t("sidebar.customerVoice.customerVoiceVolume")}
                  tooltip={t("sidebar.customerVoice.customerVoiceVolumeTooltip")}
                  icon={<SoundOutlined />}
                  value={percentageStr(displayedVolume)}
                  showValueOnExpended
                >
                  <StyledSlider
                    min={0}
                    max={200}
                    disabled={isLoading || isError}
                    value={displayedVolume}
                    onChange={handleVolumeChange}
                    onChangeComplete={handleVolumeChangeComplete}
                    tooltip={{ formatter: percentageStr }}
                    aria-label={t("sidebar.customerVoice.customerVoiceVolume")}
                    style={{ width: "100%" }}
                  />
                </SettingPanel>

                <SettingPanel
                  compact={!sidebarExpanded}
                  title={t("sidebar.customerVoice.hearTranslatedVoice")}
                  tooltip={t("sidebar.customerVoice.hearTranslatedVoiceTooltip")}
                  icon={<CustomerServiceOutlined />}
                  value={capitalize(t(isCustomerAudioEnabled ? "terms.on" : "terms.off"))}
                  showValueOnExpended
                >
                  <Switch
                    size="small"
                    disabled={isLoading || isError}
                    loading={displayedIsCustomerAudioEnabled !== isCustomerAudioEnabled}
                    checked={displayedIsCustomerAudioEnabled}
                    onChange={handleCustomerAudioEnabledChange}
                    checkedChildren={capitalize(t("terms.on"))}
                    unCheckedChildren={capitalize(t("terms.off"))}
                  />
                </SettingPanel>
              </Space>
            )}
      </SettingGroup>
    </SettingSideBarBox>
  );
}

function percentageStr(value: number | undefined): string {
  return `${value ?? Number.NaN}%`;
}

const SettingSideBarBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.paddingXS}px;

  height: 100%;
`;

// Slider looks awful with antd's default styles. It's barely visible (especially in light mode).
// Colors are based on the <Button type="primary" /> styling.
const StyledSlider = styled(Slider)`
  margin: 0;

  & .ant-slider-track {
    background-color: ${p => p.theme.colorPrimary};
  }

  &:hover .ant-slider-track {
    background-color: ${p => p.theme.colorPrimaryHover};
  }

  &:not(.ant-slider-disabled) .ant-slider-handle::after {
    box-shadow: 0 0 0 ${p => p.theme.Slider?.handleLineWidth ?? 2}px ${p => p.theme.colorPrimary};
  }

  &:not(.ant-slider-disabled):hover .ant-slider-handle::after {
    box-shadow: 0 0 0 ${p => p.theme.Slider?.handleLineWidthHover ?? 2.5}px ${p => p.theme.colorPrimaryHover};
  }

  &.ant-slider-disabled .ant-slider-handle:hover::after {
    outline: none;
  }
`;
